// src/components/wall/IndeviduealTableComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT } from "../../../lib/api-url";
import useIndividualStream from "../../shared/hooks/useIndividualSocket";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";
const REQUIRE_API_NAME = false;

// rank → points: 1→60, 2→55, ..., 12→5, 13+→0
const pointsForRank = (rank) => {
  if (!Number.isFinite(rank)) return 0;
  if (rank < 1) return 0;
  if (rank === 1) return 60;
  if (rank === 2) return 55;
  const v = 60 - (rank - 1) * 5;
  return Math.max(0, v);
};

// helpers
const makeSocketUrl = (base, path = "") => {
  if (!base) return "";
  const hasProto = /^(https?:|wss?:)/i.test(base);
  const origin = hasProto ? base : `http://${base}`;
  return `${origin}`;
};

function ensureObject(maybe) {
  if (!maybe) return null;
  if (typeof maybe === "string") {
    try {
      return JSON.parse(maybe);
    } catch {
      return null;
    }
  }
  return maybe;
}

function parseRiskEvent(frame) {
  const obj = ensureObject(frame) || frame;
  const re = obj?.RiskEvent || {};
  const login = Number(re?.login);
  const equity = Number(re?.new_value);

  // Balance: prefer corrected_value; else parse from message.
  let balance = Number(re?.corrected_value);
  if (!Number.isFinite(balance)) {
    const m = String(re?.message || "").match(/Balance=\$([0-9.\-]+)/i);
    if (m) balance = Number(m[1]);
  }

  return {
    login: Number.isFinite(login) ? login : null,
    equity: Number.isFinite(equity) ? equity : null,
    balance: Number.isFinite(balance) ? balance : null,
    ts: re?.timestamp || Date.now(),
  };
}

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "-";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function IndeviduealTableComponent() {
  // accounts keyed by login -> { login, username, equity, balance, profileImage }
  const [accountsByLogin, setAccountsByLogin] = useState({});
  const nameCacheRef = useRef(new Map());
  const prevRanksRef = useRef(new Map());
  const hasMountedRef = useRef(false);

  // 1) Initialize from Finalists API: seed rows + names
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.users.getFinalistsData);
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];

        const seeded = {};
        for (const it of items) {
          const login = Number(it?.account_login);
          if (!Number.isFinite(login)) continue;

          const fullName = `${it?.first_name ?? ""} ${
            it?.last_name ?? ""
          }`.trim();
          const displayName = fullName || it?.username || String(login);
          nameCacheRef.current.set(login, displayName);

          // Seed initial numbers from API (socket will override as it streams)
          const equity = Number(it?.equity);
          const balance = Number(it?.balance);

          seeded[login] = {
            login,
            username: displayName,
            equity: Number.isFinite(equity) ? equity : null,
            balance: Number.isFinite(balance) ? balance : null,
            profileImage: DEFAULT_AVATAR,
          };
        }

        setAccountsByLogin(seeded);
      } catch (e) {
        // swallow; UI still works when socket starts sending frames
        console.error("Finalists init failed:", e);
      }
    })();
  }, []);

  // 2) Live socket (from .env) to update balances/equities in real time
  const INDIVIDUAL_ORIGIN = process.env.REACT_APP_INDIVIDUAL_SOCKET; // e.g. 192.168.50.28:8080
  const individualUrl = makeSocketUrl(INDIVIDUAL_ORIGIN);

  const { lastItem } = useIndividualStream({
    url: individualUrl,
    token:
      (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
      "",
    enabled: true,
  });

  useEffect(() => {
    if (!lastItem) return;
    const evt = parseRiskEvent(lastItem);
    if (!evt?.login) return;

    setAccountsByLogin((prev) => {
      const old = prev[evt.login];

      // Keep any name we learned from API; otherwise fallback to login
      const username =
        nameCacheRef.current.get(evt.login) ||
        old?.username ||
        String(evt.login);

      return {
        ...prev,
        [evt.login]: {
          login: evt.login,
          username,
          equity: evt.equity ?? old?.equity ?? null,
          balance: evt.balance ?? old?.balance ?? null,
          profileImage: old?.profileImage || DEFAULT_AVATAR,
        },
      };
    });
  }, [lastItem]);

  // 3) Rank strictly from the current numbers (socket wins)
  const ranked = useMemo(() => {
    const rows = Object.values(accountsByLogin)
      .map((acc) => ({
        id: acc.login,
        login: acc.login,
        username: acc.username || String(acc.login),
        equity: Number(acc.equity ?? 0),
        balance: Number(acc.balance ?? 0),
        profileImage: acc.profileImage || DEFAULT_AVATAR,
        _hasApi: nameCacheRef.current.has(acc.login),
      }))
      .filter((r) => (REQUIRE_API_NAME ? r._hasApi : true));

    rows.sort((a, b) => b.equity - a.equity);
    return rows.slice(0, 20).map((u, i) => ({
      ...u,
      rank: i + 1,
      points: pointsForRank(i + 1),
    }));
  }, [accountsByLogin]);

  // 4) Pulse rows that changed rank
  const swappedIds = useMemo(() => {
    const prev = prevRanksRef.current;
    const changed = new Set();
    for (const u of ranked) {
      const prevRank = prev.get(u.id);
      if (prevRank != null && prevRank !== u.rank) changed.add(u.id);
    }
    const next = new Map();
    for (const u of ranked) next.set(u.id, u.rank);
    prevRanksRef.current = next;
    return changed;
  }, [ranked]);

  const top20 = ranked;
  const firstTen = top20.slice(0, 10);
  const nextTen = top20.slice(10, 20);

  return (
    <div style={{ width: "100%" }}>
      <style>{css}</style>

      <div className="tables-container w-full flex items-center justify-center">
        <div className="flex flex-row-reverse gap-[150px] w-[1085px] min-h-[703px]">
          <div className="list-scroll p-8 flex flex-col items-center justify-start gap-1 relative h-full">
            <HeaderPlaque />
            {firstTen.map((user, index) => (
              <RowCard
                key={user.id}
                user={user}
                animationDelay={index * 80}
                swapped={swappedIds.has(user.id)}
                hasMountedRef={hasMountedRef}
              />
            ))}
          </div>

          <div className="list-scroll p-8 flex flex-col items-center justify-start gap-1 max-h-[600px] relative">
            <HeaderPlaque />
            {nextTen.map((user, index) => (
              <RowCard
                key={user.id}
                user={user}
                animationDelay={index * 80}
                swapped={swappedIds.has(user.id)}
                hasMountedRef={hasMountedRef}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderPlaque() {
  return (
    <div className="table-header-container absolute top-[12px] left-[40px] w-[281px] flex justify-center items-center gap-[33px]">
      <img
        src="/container/table-header-container.svg"
        className="w-[281px] absolute z-[0]"
        alt=""
      />
      <div className="text-container flex justify-center items-center gap-[33px] relative z-10">
        <span className="text-[#FFFFFF] text-[10px] font-bold">امتیاز</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">اکوییتی</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">بالانس</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">رتبه</span>
      </div>
    </div>
  );
}

function RowCard({ user, animationDelay = 0, swapped = false, hasMountedRef }) {
  const mounted = hasMountedRef.current;
  useEffect(() => {
    hasMountedRef.current = true;
  }, [hasMountedRef]);

  const initialAnim = mounted ? false : { opacity: 0, x: -30 };

  return (
    <motion.div
      className="relative new-row"
      initial={initialAnim}
      animate={{ opacity: 1, x: 0, scale: swapped ? [1, 1.03, 1] : 1 }}
      transition={{
        opacity: { duration: mounted ? 0 : 0.42, delay: animationDelay / 1000 },
        x: { duration: mounted ? 0 : 0.42, delay: animationDelay / 1000 },
        scale: swapped
          ? { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }
          : undefined,
      }}
      layout
      layoutDependency={user.rank}
      layoutTransition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {/* background container image */}
      <img
        className="w-[436px]"
        src="/container/player-box-container.png"
        alt=""
      />

      {/* avatar */}
      <img
        src={user.profileImage || DEFAULT_AVATAR}
        className="w-[43px] absolute right-[20px] bottom-[23px] z-10"
        alt=""
      />

      {/* overlayed content */}
      <div className="absolute top-[-10px] left-0 w-full h-full flex items-center justify-start gap-[24px] flex-row-reverse">
        {/* username (RTL) */}
        <span className="text-[#FFFFFF] text-[8px] font-bold mr-[90px] w-[120px] truncate">
          {user.username}
        </span>

        {/* stats (Points, Equity, Balance, Rank) */}
        <div className="w-full flex justify-end items-center gap-[30px] ">
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.points)}
          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.equity)}
          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.balance)}
          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {user.rank}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

/* styles preserved verbatim */
const css = `
.list-scroll::-webkit-scrollbar { width: 0; height: 0; }
.list-scroll {
  max-height: 75vh;
  width: 100%;
  overflow-y: visible;
  overflow-x: hidden;
  padding-right: 8px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.table-header-container { pointer-events: none; }

.new-row { width: 436px; height: 66px; }

/* You can keep or remove the older trapezoid styles if unused elsewhere */
`;
